import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  FileText, Folder, FileInput, FileOutput, FileSearch, FileDiff, 
  Hash, Binary, CheckCircle, AlertCircle, Clock, Scan, 
  RefreshCw, Upload, Download, X, ChevronDown, ChevronUp 
} from 'lucide-react';

const ForensicIntegrity = () => {
  // App state
  const [activeTab, setActiveTab] = useState('hashCompare');
  const [theme, setTheme] = useState('dark');
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);

  // Hash comparison state
  const [hash1, setHash1] = useState('');
  const [hash2, setHash2] = useState('');
  const [hashAlgorithm, setHashAlgorithm] = useState('sha256');
  const [hashResult, setHashResult] = useState(null);

  // File comparison state
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [fileComparisonResult, setFileComparisonResult] = useState(null);
  const [fileStats, setFileStats] = useState({ file1: null, file2: null });

  // Base64 state
  const [base64Input, setBase64Input] = useState('');
  const [base64Output, setBase64Output] = useState('');
  const [base64Mode, setBase64Mode] = useState('encode');
  const [base64File, setBase64File] = useState(null);

  // Folder checksum state
  const [folderFiles, setFolderFiles] = useState([]);
  const [folderChecksum, setFolderChecksum] = useState(null);
  const folderInputRef = useRef(null);

  // Animation refs
  const scanBarRef = useRef(null);
  const resultCardRef = useRef(null);

  // Available hash algorithms
  const hashAlgorithms = ['md5', 'sha1', 'sha256', 'sha512'];

  // Toggle theme
  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Simulate scanning animation
  const startScanning = () => {
    setIsScanning(true);
    setScanProgress(0);
    
    const interval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsScanning(false);
          return 100;
        }
        return prev + 10;
      });
    }, 150);
  };

  // Compare two hashes
  const compareHashes = () => {
    startScanning();
    
    setTimeout(() => {
      const result = {
        match: hash1 === hash2,
        hash1,
        hash2,
        algorithm: hashAlgorithm,
        timestamp: new Date().toLocaleString()
      };
      setHashResult(result);
      
      // Trigger animation
      if (resultCardRef.current) {
        resultCardRef.current.classList.add('animate-pulse');
        setTimeout(() => {
          resultCardRef.current.classList.remove('animate-pulse');
        }, 1000);
      }
    }, 1200);
  };

  // Handle file upload for comparison
  const handleFileUpload = (e, fileNumber) => {
    const file = e.target.files[0];
    if (!file) return;

    if (fileNumber === 1) {
      setFile1(file);
      setFileStats(prev => ({
        ...prev,
        file1: {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: new Date(file.lastModified).toLocaleString()
        }
      }));
    } else {
      setFile2(file);
      setFileStats(prev => ({
        ...prev,
        file2: {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: new Date(file.lastModified).toLocaleString()
        }
      }));
    }
  };

  // Compare two files
  const compareFiles = async () => {
    if (!file1 || !file2) return;
    
    startScanning();
    
    setTimeout(async () => {
      try {
        const text1 = await file1.text();
        const text2 = await file2.text();
        
        // Simple comparison - in a real app you'd use a more sophisticated diff algorithm
        const lines1 = text1.split('\n');
        const lines2 = text2.split('\n');
        
        const maxLines = Math.max(lines1.length, lines2.length);
        const differences = [];
        
        for (let i = 0; i < maxLines; i++) {
          if (lines1[i] !== lines2[i]) {
            differences.push({
              line: i + 1,
              file1: lines1[i] || '[MISSING]',
              file2: lines2[i] || '[MISSING]'
            });
          }
        }
        
        const result = {
          filesMatch: differences.length === 0,
          totalLines: maxLines,
          differences,
          stats: {
            file1: { lines: lines1.length },
            file2: { lines: lines2.length }
          },
          timestamp: new Date().toLocaleString()
        };
        
        setFileComparisonResult(result);
      } catch (error) {
        console.error('Error comparing files:', error);
        setFileComparisonResult({
          error: 'Failed to compare files. Make sure they are text files.'
        });
      }
    }, 1200);
  };

  // Handle Base64 operations
  const handleBase64Operation = () => {
    startScanning();
    
    setTimeout(() => {
      try {
        if (base64Mode === 'encode') {
          setBase64Output(btoa(base64Input));
        } else {
          setBase64Output(atob(base64Input));
        }
      } catch (error) {
        setBase64Output('Error: Invalid input for selected operation');
      }
    }, 1200);
  };

  // Handle file for Base64 operations
  const handleBase64File = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setBase64File(file);
    startScanning();
    
    setTimeout(async () => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        bytes.forEach(byte => binary += String.fromCharCode(byte));
        setBase64Output(btoa(binary));
      } catch (error) {
        console.error('Error processing file:', error);
        setBase64Output('Error processing file');
      }
    }, 1200);
  };

  // Calculate checksum for a folder (simulated in frontend)
  const calculateFolderChecksum = () => {
    if (folderFiles.length === 0) return;
    
    startScanning();
    
    setTimeout(() => {
      // In a real app, you would actually calculate checksums for all files
      // This is a simulation for the UI
      const mockChecksum = {
        totalFiles: folderFiles.length,
        totalSize: folderFiles.reduce((sum, file) => sum + file.size, 0),
        checksum: `sha256:${Math.random().toString(36).substring(2, 18)}`,
        lastModified: new Date(Math.max(...folderFiles.map(f => f.lastModified))).toLocaleString(),
        files: folderFiles.map(file => ({
          name: file.name,
          size: file.size,
          lastModified: new Date(file.lastModified).toLocaleString(),
          checksum: `md5:${Math.random().toString(36).substring(2, 10)}`
        }))
      };
      
      setFolderChecksum(mockChecksum);
    }, 1500);
  };

  // Handle folder selection
  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files);
    setFolderFiles(files);
  };

  // Reset operations
  const resetOperation = () => {
    switch (activeTab) {
      case 'hashCompare':
        setHash1('');
        setHash2('');
        setHashResult(null);
        break;
      case 'fileCompare':
        setFile1(null);
        setFile2(null);
        setFileComparisonResult(null);
        setFileStats({ file1: null, file2: null });
        break;
      case 'base64':
        setBase64Input('');
        setBase64Output('');
        setBase64File(null);
        break;
      case 'folderChecksum':
        setFolderFiles([]);
        setFolderChecksum(null);
        break;
    }
  };

  // Effect for scanning animation
  useEffect(() => {
    if (isScanning && scanBarRef.current) {
      scanBarRef.current.style.width = `${scanProgress}%`;
    }
  }, [isScanning, scanProgress]);

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
     
      {/* Main Content */}
      <main className="container mx-auto p-4">
        {/* Navigation Tabs */}
        <div className={`mb-6 rounded-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => setActiveTab('hashCompare')}
              className={`flex-1 py-3 px-4 font-medium flex items-center justify-center space-x-2 ${activeTab === 'hashCompare' ? (theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700') : ''}`}
            >
              <Hash size={18} />
              <span>Hash Compare</span>
            </button>
            <button
              onClick={() => setActiveTab('fileCompare')}
              className={`flex-1 py-3 px-4 font-medium flex items-center justify-center space-x-2 ${activeTab === 'fileCompare' ? (theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700') : ''}`}
            >
              <FileDiff size={18} />
              <span>File Compare</span>
            </button>
            <button
              onClick={() => setActiveTab('base64')}
              className={`flex-1 py-3 px-4 font-medium flex items-center justify-center space-x-2 ${activeTab === 'base64' ? (theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700') : ''}`}
            >
              <Binary size={18} />
              <span>Base64</span>
            </button>
            <button
              onClick={() => setActiveTab('folderChecksum')}
              className={`flex-1 py-3 px-4 font-medium flex items-center justify-center space-x-2 ${activeTab === 'folderChecksum' ? (theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700') : ''}`}
            >
              <Folder size={18} />
              <span>Folder Checksum</span>
            </button>
          </div>
        </div>

        {/* Scanning Indicator */}
        {isScanning && (
          <div className={`mb-6 rounded-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <div className="p-4">
              <div className="flex items-center space-x-3">
                <RefreshCw className="animate-spin text-blue-500" />
                <span>Analyzing data...</span>
              </div>
              <div className={`mt-2 h-2 rounded-full ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div 
                  ref={scanBarRef}
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300 ease-out"
                  style={{ width: '0%' }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Hash Comparison Tab */}
        {activeTab === 'hashCompare' && (
          <div className={`rounded-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow mb-6`}>
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <Hash className="mr-2" size={20} />
                Hash Comparison
              </h2>
              <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Compare two cryptographic hashes to verify their integrity and detect any discrepancies.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    First Hash
                  </label>
                  <textarea
                    value={hash1}
                    onChange={(e) => setHash1(e.target.value)}
                    className={`w-full p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    rows={4}
                    placeholder="Paste first hash here..."
                  ></textarea>
                </div>
                <div>
                  <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Second Hash
                  </label>
                  <textarea
                    value={hash2}
                    onChange={(e) => setHash2(e.target.value)}
                    className={`w-full p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    rows={4}
                    placeholder="Paste second hash here..."
                  ></textarea>
                </div>
              </div>

              <div className="mb-6">
                <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Hash Algorithm
                </label>
                <div className="flex flex-wrap gap-2">
                  {hashAlgorithms.map(alg => (
                    <button
                      key={alg}
                      onClick={() => setHashAlgorithm(alg)}
                      className={`px-4 py-2 rounded-lg ${hashAlgorithm === alg ? 'bg-blue-600 text-white' : theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                    >
                      {alg.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={compareHashes}
                  disabled={!hash1 || !hash2}
                  className={`px-6 py-3 rounded-lg bg-blue-600 text-white font-medium flex items-center ${(!hash1 || !hash2) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                >
                  <FileSearch className="mr-2" size={18} />
                  Compare Hashes
                </button>
                <button
                  onClick={resetOperation}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                  <X className="mr-2" size={18} />
                  Reset
                </button>
              </div>
            </div>

            {/* Results */}
            {hashResult && (
              <div 
                ref={resultCardRef}
                className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} p-6`}
              >
                <h3 className="text-lg font-bold mb-4 flex items-center">
                  {hashResult.match ? (
                    <CheckCircle className="mr-2 text-green-500" size={20} />
                  ) : (
                    <AlertCircle className="mr-2 text-red-500" size={20} />
                  )}
                  Comparison Results
                </h3>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Algorithm</p>
                      <p className="font-mono">{hashResult.algorithm.toUpperCase()}</p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Result</p>
                      <p className={hashResult.match ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}>
                        {hashResult.match ? 'Hashes Match' : 'Hashes Do Not Match'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Timestamp</p>
                      <p>{hashResult.timestamp}</p>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h4 className="font-medium mb-2">Hash Values</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Hash 1</p>
                      <p className="font-mono break-all">{hashResult.hash1}</p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Hash 2</p>
                      <p className="font-mono break-all">{hashResult.hash2}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* File Comparison Tab */}
        {activeTab === 'fileCompare' && (
          <div className={`rounded-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow mb-6`}>
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <FileDiff className="mr-2" size={20} />
                File Comparison
              </h2>
              <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Upload two files to compare their contents and identify differences with forensic precision.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    First File
                  </label>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${theme === 'dark' ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'} transition-colors cursor-pointer`}>
                    {file1 ? (
                      <div className="flex flex-col items-center">
                        <FileText className="text-blue-500 mb-2" size={32} />
                        <p className="font-medium truncate max-w-full">{file1.name}</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {(file1.size / 1024).toFixed(2)} KB
                        </p>
                        <button
                          onClick={() => setFile1(null)}
                          className="mt-3 px-3 py-1 text-sm rounded bg-red-500 text-white hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <Upload className="mx-auto mb-3 text-blue-500" size={32} />
                        <p className="mb-1">Click to upload file</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>or drag and drop</p>
                        <input
                          type="file"
                          onChange={(e) => handleFileUpload(e, 1)}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  {fileStats.file1 && (
                    <div className={`mt-3 p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Size</p>
                          <p className="text-sm">{(fileStats.file1.size / 1024).toFixed(2)} KB</p>
                        </div>
                        <div>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Modified</p>
                          <p className="text-sm flex items-center">
                            <Clock className="mr-1" size={12} />
                            {fileStats.file1.lastModified}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Second File
                  </label>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${theme === 'dark' ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'} transition-colors cursor-pointer`}>
                    {file2 ? (
                      <div className="flex flex-col items-center">
                        <FileText className="text-blue-500 mb-2" size={32} />
                        <p className="font-medium truncate max-w-full">{file2.name}</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {(file2.size / 1024).toFixed(2)} KB
                        </p>
                        <button
                          onClick={() => setFile2(null)}
                          className="mt-3 px-3 py-1 text-sm rounded bg-red-500 text-white hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <Upload className="mx-auto mb-3 text-blue-500" size={32} />
                        <p className="mb-1">Click to upload file</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>or drag and drop</p>
                        <input
                          type="file"
                          onChange={(e) => handleFileUpload(e, 2)}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  {fileStats.file2 && (
                    <div className={`mt-3 p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Size</p>
                          <p className="text-sm">{(fileStats.file2.size / 1024).toFixed(2)} KB</p>
                        </div>
                        <div>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Modified</p>
                          <p className="text-sm flex items-center">
                            <Clock className="mr-1" size={12} />
                            {fileStats.file2.lastModified}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={compareFiles}
                  disabled={!file1 || !file2}
                  className={`px-6 py-3 rounded-lg bg-blue-600 text-white font-medium flex items-center ${(!file1 || !file2) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                >
                  <FileSearch className="mr-2" size={18} />
                  Compare Files
                </button>
                <button
                  onClick={resetOperation}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                  <X className="mr-2" size={18} />
                  Reset
                </button>
              </div>
            </div>

            {/* Results */}
            {fileComparisonResult && (
              <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} p-6`}>
                <h3 className="text-lg font-bold mb-4 flex items-center">
                  {fileComparisonResult.error ? (
                    <AlertCircle className="mr-2 text-red-500" size={20} />
                  ) : fileComparisonResult.filesMatch ? (
                    <CheckCircle className="mr-2 text-green-500" size={20} />
                  ) : (
                    <AlertCircle className="mr-2 text-yellow-500" size={20} />
                  )}
                  Comparison Results
                </h3>

                {fileComparisonResult.error ? (
                  <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className="text-red-500">{fileComparisonResult.error}</p>
                  </div>
                ) : (
                  <>
                    <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Result</p>
                          <p className={fileComparisonResult.filesMatch ? 'text-green-500 font-medium' : 'text-yellow-500 font-medium'}>
                            {fileComparisonResult.filesMatch ? 'Files Match Exactly' : `${fileComparisonResult.differences.length} Differences Found`}
                          </p>
                        </div>
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Lines</p>
                          <p>{fileComparisonResult.totalLines}</p>
                        </div>
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Timestamp</p>
                          <p>{fileComparisonResult.timestamp}</p>
                        </div>
                      </div>
                    </div>

                    {!fileComparisonResult.filesMatch && (
                      <div className="mb-6">
                        <h4 className="font-medium mb-3">Differences</h4>
                        <div className={`rounded-lg overflow-hidden border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                          <div className={`grid grid-cols-12 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} p-2 font-medium text-sm`}>
                            <div className="col-span-1">Line</div>
                            <div className="col-span-5">File 1</div>
                            <div className="col-span-5">File 2</div>
                            <div className="col-span-1">Status</div>
                          </div>
                          <div className="max-h-96 overflow-y-auto">
                            {fileComparisonResult.differences.map((diff, index) => (
                              <div 
                                key={index} 
                                className={`grid grid-cols-12 p-2 text-sm border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} ${index % 2 === 0 ? (theme === 'dark' ? 'bg-gray-800' : 'bg-white') : (theme === 'dark' ? 'bg-gray-750' : 'bg-gray-50')}`}
                              >
                                <div className="col-span-1 font-mono">{diff.line}</div>
                                <div className="col-span-5 font-mono">
                                  {diff.file1 === '[MISSING]' ? (
                                    <span className="text-red-500">{diff.file1}</span>
                                  ) : (
                                    diff.file1
                                  )}
                                </div>
                                <div className="col-span-5 font-mono">
                                  {diff.file2 === '[MISSING]' ? (
                                    <span className="text-red-500">{diff.file2}</span>
                                  ) : (
                                    diff.file2
                                  )}
                                </div>
                                <div className="col-span-1">
                                  {diff.file1 === '[MISSING]' || diff.file2 === '[MISSING]' ? (
                                    <span className="text-red-500">Missing</span>
                                  ) : (
                                    <span className="text-yellow-500">Changed</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <h4 className="font-medium mb-2">File Statistics</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>File 1</p>
                          <p className="font-medium">{fileStats.file1.name}</p>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Lines</p>
                              <p>{fileComparisonResult.stats.file1.lines}</p>
                            </div>
                            <div>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Size</p>
                              <p>{(fileStats.file1.size / 1024).toFixed(2)} KB</p>
                            </div>
                          </div>
                        </div>
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>File 2</p>
                          <p className="font-medium">{fileStats.file2.name}</p>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Lines</p>
                              <p>{fileComparisonResult.stats.file2.lines}</p>
                            </div>
                            <div>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Size</p>
                              <p>{(fileStats.file2.size / 1024).toFixed(2)} KB</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Base64 Tab */}
        {activeTab === 'base64' && (
          <div className={`rounded-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow mb-6`}>
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <Binary className="mr-2" size={20} />
                Base64 Encoding/Decoding
              </h2>
              <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Encode text to Base64 or decode Base64 back to text. You can also process files.
              </p>

              <div className="mb-6">
                <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Operation Mode
                </label>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setBase64Mode('encode')}
                    className={`px-6 py-2 rounded-lg ${base64Mode === 'encode' ? 'bg-blue-600 text-white' : theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                  >
                    Encode
                  </button>
                  <button
                    onClick={() => setBase64Mode('decode')}
                    className={`px-6 py-2 rounded-lg ${base64Mode === 'decode' ? 'bg-blue-600 text-white' : theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                  >
                    Decode
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {base64Mode === 'encode' ? 'Text to Encode' : 'Base64 to Decode'}
                </label>
                <textarea
                  value={base64Input}
                  onChange={(e) => setBase64Input(e.target.value)}
                  className={`w-full p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  rows={6}
                  placeholder={base64Mode === 'encode' ? 'Enter text to encode to Base64...' : 'Enter Base64 to decode to text...'}
                ></textarea>
              </div>

              <div className="mb-6">
                <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Process File
                </label>
                <div className={`border-2 border-dashed rounded-lg p-6 text-center ${theme === 'dark' ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'} transition-colors cursor-pointer`}>
                  {base64File ? (
                    <div className="flex flex-col items-center">
                      <FileText className="text-blue-500 mb-2" size={32} />
                      <p className="font-medium truncate max-w-full">{base64File.name}</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {(base64File.size / 1024).toFixed(2)} KB
                      </p>
                      <button
                        onClick={() => setBase64File(null)}
                        className="mt-3 px-3 py-1 text-sm rounded bg-red-500 text-white hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="mx-auto mb-3 text-blue-500" size={32} />
                      <p className="mb-1">Click to upload file</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>File will be {base64Mode === 'encode' ? 'encoded' : 'decoded'}</p>
                      <input
                        type="file"
                        onChange={handleBase64File}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleBase64Operation}
                  disabled={!base64Input && !base64File}
                  className={`px-6 py-3 rounded-lg bg-blue-600 text-white font-medium flex items-center ${(!base64Input && !base64File) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                >
                  {base64Mode === 'encode' ? (
                    <FileInput className="mr-2" size={18} />
                  ) : (
                    <FileOutput className="mr-2" size={18} />
                  )}
                  {base64Mode === 'encode' ? 'Encode' : 'Decode'}
                </button>
                <button
                  onClick={resetOperation}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                  <X className="mr-2" size={18} />
                  Reset
                </button>
              </div>
            </div>

            {/* Results */}
            {(base64Output || base64File) && (
              <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} p-6`}>
                <h3 className="text-lg font-bold mb-4">
                  {base64Mode === 'encode' ? 'Base64 Encoded Output' : 'Decoded Output'}
                </h3>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Operation</p>
                      <p>{base64Mode === 'encode' ? 'Encode' : 'Decode'}</p>
                    </div>
                    {base64File && (
                      <div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>File</p>
                        <p className="truncate">{base64File.name}</p>
                      </div>
                    )}
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Timestamp</p>
                      <p>{new Date().toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Output
                  </label>
                  <textarea
                    value={base64Output}
                    readOnly
                    className={`w-full p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} font-mono`}
                    rows={8}
                  ></textarea>
                </div>

                {base64Output && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(base64Output);
                    }}
                    className={`px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                  >
                    Copy to Clipboard
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Folder Checksum Tab */}
        {activeTab === 'folderChecksum' && (
          <div className={`rounded-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow mb-6`}>
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <Folder className="mr-2" size={20} />
                Folder Checksum
              </h2>
              <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Calculate cryptographic checksums for all files in a folder to verify integrity and detect changes.
              </p>

              <div className="mb-6">
                <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Folder
                </label>
                <div 
                  onClick={() => folderInputRef.current.click()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center ${theme === 'dark' ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'} transition-colors cursor-pointer`}
                >
                  {folderFiles.length > 0 ? (
                    <div className="flex flex-col items-center">
                      <Folder className="text-blue-500 mb-2" size={32} />
                      <p className="font-medium">{folderFiles.length} files selected</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {Math.round(folderFiles.reduce((sum, file) => sum + file.size, 0) / 1024)} KB total
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderFiles([]);
                        }}
                        className="mt-3 px-3 py-1 text-sm rounded bg-red-500 text-white hover:bg-red-600"
                      >
                        Clear
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Folder className="mx-auto mb-3 text-blue-500" size={32} />
                      <p className="mb-1">Click to select folder</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>All files will be processed</p>
                    </div>
                  )}
                  <input
                    type="file"
                    ref={folderInputRef}
                    onChange={handleFolderSelect}
                    className="hidden"
                    // @ts-ignore
                    webkitdirectory="true"
                    directory="true"
                    multiple
                  />
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={calculateFolderChecksum}
                  disabled={folderFiles.length === 0}
                  className={`px-6 py-3 rounded-lg bg-blue-600 text-white font-medium flex items-center ${folderFiles.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                >
                  <Hash className="mr-2" size={18} />
                  Calculate Checksum
                </button>
                <button
                  onClick={resetOperation}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                  <X className="mr-2" size={18} />
                  Reset
                </button>
              </div>
            </div>

            {/* Results */}
            {folderChecksum && (
              <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} p-6`}>
                <h3 className="text-lg font-bold mb-4 flex items-center">
                  <CheckCircle className="mr-2 text-green-500" size={20} />
                  Folder Checksum Results
                </h3>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Files</p>
                      <p>{folderChecksum.totalFiles}</p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Size</p>
                      <p>{(folderChecksum.totalSize / 1024).toFixed(2)} KB</p>
                    </div>
                    <div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Last Modified</p>
                      <p className="flex items-center">
                        <Clock className="mr-1" size={12} />
                        {folderChecksum.lastModified}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Folder Checksum
                    </label>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(folderChecksum.checksum);
                      }}
                      className="text-sm px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Copy
                    </button>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className="font-mono break-all">{folderChecksum.checksum}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Files in Folder
                  </label>
                  <div className={`rounded-lg overflow-hidden border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className={`grid grid-cols-12 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} p-2 font-medium text-sm`}>
                      <div className="col-span-6">Filename</div>
                      <div className="col-span-2">Size</div>
                      <div className="col-span-3">Modified</div>
                      <div className="col-span-1">Checksum</div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {folderChecksum.files.map((file, index) => (
                        <FileRow 
                          key={index} 
                          file={file} 
                          theme={theme} 
                          isLast={index === folderChecksum.files.length - 1}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// Component for folder file rows with expandable checksum
const FileRow = ({ file, theme, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <>
      <div 
        className={`grid grid-cols-12 p-2 text-sm ${!isLast ? `border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}` : ''} ${theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} cursor-pointer`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="col-span-6 truncate">{file.name}</div>
        <div className="col-span-2">{(file.size / 1024).toFixed(2)} KB</div>
        <div className="col-span-3 truncate">{file.lastModified}</div>
        <div className="col-span-1 flex justify-end">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(file.checksum);
            }}
            className="p-1 rounded hover:bg-blue-600 hover:text-white"
            title="Copy checksum"
          >
            <Copy size={14} />
          </button>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>
      {expanded && (
        <div className={`p-2 ${theme === 'dark' ? 'bg-gray-750' : 'bg-gray-50'} border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-start">
            <div className={`mr-2 mt-1 p-1 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <Hash size={12} />
            </div>
            <div className="flex-1">
              <p className={`text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Checksum</p>
              <p className="font-mono text-sm break-all">{file.checksum}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Small Copy icon component since it's not in react-lucide
const Copy = ({ size = 16 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

export default ForensicIntegrity;